import { FC, useEffect } from "react";
import { BindInstructionsComponentsToSaveInfoProps } from "./PolicyQuoteBindInstructionsTypes";
import { useAtomFamily } from "../../../../hooks/useAtomFamily";
import { INSURED_ATOM_KEY } from "../../../../utilities/queryStringsHash";
import { GlobalInsuredAtomFamily } from "../../InsuredAtoms";
import { updatePolicyQuoteInformation } from "../updatesPolicyQuoteFunctions";
import { AlertEnums } from "../../../../dtos/alert-enums";
import ProgramValidationForAlerts from "../PolicyValidation/ProgramValidationForAlerts";
import { AlertProps } from "../../../TrueUI/Alerts/Alert";
import { evaluateRule } from "../PolicyValidation/ValidationEngineFunctions";
import { isEmptyValue } from "../../../../utilities/conditionalSupportFunctions";

const ReviewPolicyValidations: FC<
  BindInstructionsComponentsToSaveInfoProps
> = ({ tabKey, insuredId }) => {
  const insuredIdAtomKey = `${INSURED_ATOM_KEY} ${tabKey}`;
  const { getAtom, setAtom, setComponentTriggers } = useAtomFamily(
    GlobalInsuredAtomFamily(insuredIdAtomKey)
  );

  const getBanner = (validationResults: AlertProps[]) => {
    if (validationResults.length > 1) {
      const type = validationResults.some(
        (validation) => validation.type === AlertEnums.WARNING
      )
        ? AlertEnums.WARNING
        : AlertEnums.INFORMATION;
      const modalHeight = validationResults.reduce(
        (height, validation, index) => {
          if (index < 6)
            validation.message.length > 141 ? (height += 68) : (height += 45);
          return height;
        },
        0
      );
      const message =
        "More than one warning exists for this quote. Click here for more information";
      const body = () => (
        <ProgramValidationForAlerts
          validationResults={validationResults}
          insuredId={insuredId ?? 0}
        />
      );

      return [
        {
          type,
          message,
          showCloseIcon: false,
          modalTitle: "Quote Validation Messages",
          modalContent: body(),
          modalHeight: modalHeight + 230,
        } as AlertProps,
      ];
    }
    return validationResults;
  };

  const getProgramValidationList = () => {
    const atomValue = getAtom();
    const policyJSON = atomValue?.policyQuoteInformation?.policyQuote;
    const validations = policyJSON?.configuration?.programValidationList ?? [];

    const validValidations = validations.filter((validation) =>
      evaluateRule(validation.validationQuery, policyJSON)
    );

    const validationResults: AlertProps[] = validValidations.map(
      (validation) => ({
        type: validation.validationType,
        message: validation.validationMessage,
        showCloseIcon: false,
      })
    );
    return validationResults;
  };

  const removePolicyValidationsFromAtom = () => {
    const atomValue = getAtom();
    const updatedAtomValue = updatePolicyQuoteInformation(
      atomValue,
      "policyValidations",
      []
    );
    setAtom(updatedAtomValue);
    setComponentTriggers(["alertAtomComponent"]);
  };

  const reviewPolicyValidations = () => {
    const atomValue = getAtom();
    const validationResults = getProgramValidationList();

    if (!isEmptyValue(validationResults)) {
      const newAtomValue = updatePolicyQuoteInformation(
        atomValue,
        "policyValidations",
        getBanner(validationResults)
      );
      setAtom(newAtomValue);
      setComponentTriggers(["alertAtomComponent"]);
    }
  };

  useEffect(() => {
    reviewPolicyValidations();
    return () => {
      removePolicyValidationsFromAtom();
    };
  }, []);
  return null;
};
export default ReviewPolicyValidations;
